import {
  ChannelLogEntry,
  ChannelName,
  ChannelRuntimeReport,
} from '../api/channels';
import { parseInstant } from '../utils/datetime';

export interface ChannelReportingLogInput {
  channelId?: string | null;
  channelName: ChannelName | string;
  lastError?: string | null;
  lastSyncAt?: string | null;
  report: ChannelRuntimeReport | null;
}

function missingStamp(value: string | null | undefined): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

/** A real instant from the payload, or null. Never substitutes the wall clock. */
function knownInstant(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return parseInstant(trimmed) ? trimmed : null;
}

function instantMillis(value: string | null): number | null {
  const parsed = parseInstant(value);
  return parsed ? parsed.getTime() : null;
}

/**
 * Recent Logs rows for the channel detail page.
 * Missing `generated_at` / `last_sync_at` / `last_seen_at` stay null so the
 * table can show an unknown time. Undated rows sort after dated ones.
 */
export function buildChannelReportingLogs(input: ChannelReportingLogInput): ChannelLogEntry[] {
  const logs: ChannelLogEntry[] = [];
  const channelId = input.channelId ?? input.channelName;
  const generatedAt = knownInstant(input.report?.generated_at);

  if (input.report) {
    const activity = input.report.recent_activity;
    logs.push({
      id: `activity-${generatedAt ?? 'unknown'}`,
      channel_id: channelId,
      timestamp: generatedAt,
      action: 'channel.reporting',
      status: 'info',
      level: 'info',
      message: `Activity: ${activity.updated_last_1m} updates in 1m, ${activity.updated_last_5m} in 5m (~${input.report.throughput_per_min_last_5m}/min).`,
    });
  }

  const failures = input.report?.recent_failures ?? [];
  for (let i = 0; i < failures.length; i += 1) {
    const failure = failures[i];
    if (!failure) continue;
    const seenAt = missingStamp(failure.last_seen_at)
      ? generatedAt
      : knownInstant(failure.last_seen_at);
    logs.push({
      id: `failure-${i}-${seenAt ?? 'unknown'}`,
      channel_id: channelId,
      timestamp: seenAt,
      action: 'channel.failure',
      status: 'error',
      level: 'error',
      message: `${failure.error} (${failure.count} occurrences)`,
    });
  }

  if (!logs.length && input.lastError) {
    const syncedAt = knownInstant(input.lastSyncAt);
    logs.push({
      id: 'last-error-fallback',
      channel_id: channelId,
      timestamp: syncedAt,
      action: 'channel.last_error',
      status: 'warning',
      level: 'warning',
      message: input.lastError,
    });
  }

  return logs.sort((a, b) => {
    const at = instantMillis(a.timestamp);
    const bt = instantMillis(b.timestamp);
    if (at == null && bt == null) return 0;
    if (at == null) return 1;
    if (bt == null) return -1;
    return bt - at;
  });
}
